import React, {
  memo, useMemo, useRef, useState, useCallback,
} from 'react'
import Form, { FormConsumer } from '@active/react-ui/Form'
import FormGroup from '@active/react-ui/FormGroup'
import FormLabel from '@active/react-ui/FormLabel'
import FormField from '@active/react-ui/FormField'
import Modal from '@active/react-ui/Modal'
import ModalBody from '@active/react-ui/ModalBody'
import ModalFooter from '@active/react-ui/ModalFooter'
import Grid from '@active/react-ui/Grid'
import GridItem from '@active/react-ui/GridItem'
import Input from '@active/react-ui/Input'
import Checkbox from '@active/react-ui/Checkbox'
import Button from '@active/react-ui/Button'
import LinkButton from '@active/react-ui/LinkButton'
import {
  func, string, bool, number,
} from 'prop-types'
import { Locale, withI18n } from '@active/react-utils/i18n'
import isEmpty from 'lodash/isEmpty'
import noop from 'lodash/noop'
import { maskSenderEmail, maskSenderPhone } from '../../../../shared/utils/commonUtils'
import I18nMessage from '../../I18nMessage'
import { VerificationMethods } from '../config'
import './MFAStepTwo.scss'

function MFAStepTwo({
  emailAddress,
  phoneNumber,
  countDownNumber,
  code,
  channel,
  isTrustDevice: defaultIsTrustDevice,
  isShowBackBtn,
  handleClose,
  handleReject,
  handleConfirm,
  handleResend,
  i18n,
  ...rest
},
) {
  const [isTrustDevice, setIsTrustDevice] = useState(defaultIsTrustDevice)
  const okText = 'passport.common.btn.verify'
  const cancelText = 'passport.lib.components.common.btn.tryAnother'
  const codeInputLabel = 'passport.verification.modal.input.placeholder'
  const codeInputPlaceHolder = 'passport.verification.modal.input.code.placeholder'
  const resendText = 'passport.lib.components.common.link.resend.code'
  const title = i18n('passport.verification.modal.title')
  const checkBoxLabel = 'passport.trust.device.label.authorized'
  const desc = useMemo(() => (channel === VerificationMethods.EMAIL
    ? <I18nMessage id="passport.verification.modal.description.resendToYourEmail" params={{ 0: maskSenderEmail(emailAddress) }} />
    : <I18nMessage id="passport.phoneAuthentication.modal.description.resendToSMS" params={{ 0: maskSenderPhone(phoneNumber) }} />), [channel, emailAddress, phoneNumber])

  const countDownTips = useMemo(() => <I18nMessage className="countDown-tip" id="passport.verification.modal.countDown.tips" params={{ 0: `<span>${countDownNumber}</span>` }} />, [countDownNumber])

  const formRef = useRef()

  const onChangeTrustDevice = useCallback(e => {
    setIsTrustDevice(e.target.checked)
  }, [setIsTrustDevice])

  const handleSubmit = useCallback(async () => {
    const result = await formRef.current.validateForm({ shouldTouchFields: true })
    if (isEmpty(result)) {
      handleConfirm({
        ...formRef.current.values,
        channel,
        isTrustDevice,
      })
    }
  }, [handleConfirm, formRef, isTrustDevice, channel])

  return (
    <Modal
      title={title}
      onClose={handleClose}
      size="md"
      shown
      {...rest}
      className="mfa-step-two"
    >
      <ModalBody className="modal-body mfa-step-two-content">
        <div>
          {desc}
        </div>
        <Form
          initialValues={{
            code,
          }}
        >
          <Grid fluid rowGap={0} colGap={0}>
            <GridItem span={12} spanMd={8}>
              <FormGroup name="code" withoutLayout required>
                <FormLabel verticalStart><Locale id={codeInputLabel} /></FormLabel>
                <FormField as={Input} placeholder={i18n(codeInputPlaceHolder)} />
              </FormGroup>
            </GridItem>
            <GridItem span={12} spanMd={8}>
              <div className="mfa-step-two-code-extra">
                <LinkButton disabled={countDownNumber > 0} onClick={handleResend}>
                  <Locale id={resendText} />
                </LinkButton>
                { countDownNumber > 0 && (
                <div>
                  { countDownTips }
                </div>
                ) }
              </div>
            </GridItem>
          </Grid>
          <FormConsumer>
            {
              ({
                form,
              }) => {
                formRef.current = form
              }
            }
          </FormConsumer>
        </Form>
      </ModalBody>
      <ModalFooter>
        <div>
          <Checkbox defaultChecked={isTrustDevice} onChange={onChangeTrustDevice}>
            <Locale id={checkBoxLabel} />
          </Checkbox>
        </div>
        <div className="mfa-step-two-footer-btns">
          <Button
            type="strong"
            onClick={handleSubmit}
          >
            <Locale id={okText} />
          </Button>
          {isShowBackBtn && (
          <Button
            type="secondary"
            onClick={handleReject}
          >
            <Locale id={cancelText} />
          </Button>
          )}
        </div>
      </ModalFooter>
    </Modal>

  )
}

MFAStepTwo.propTypes = {
  channel: string.isRequired,
  handleConfirm: func.isRequired,
  handleResend: func.isRequired,
  i18n: func.isRequired,
  emailAddress: string.isRequired,
  handleClose: func.isRequired,
  phoneNumber: string,
  countDownNumber: number,
  code: string,
  isTrustDevice: bool,
  isShowBackBtn: bool,
  handleReject: func,
}

MFAStepTwo.defaultProps = {
  phoneNumber: '',
  countDownNumber: '',
  code: '',
  isTrustDevice: true,
  isShowBackBtn: true,
  handleReject: noop,
}
export default memo(withI18n(MFAStepTwo))
